// Content translation utility for database content
// This provides translations for dynamic content like product names, category names, etc.

export interface TranslationMap {
  [key: string]: {
    pt: string;
    en: string;
  };
}

// Category name translations
export const categoryTranslations: TranslationMap = {
  // Common darts store categories
  "Dardos": { pt: "Dardos", en: "Darts" },
  "Acessórios": { pt: "Acessórios", en: "Accessories" },
  "Alvos": { pt: "Alvos", en: "Dartboards" },
  "Pontas": { pt: "Pontas", en: "Tips" },
  "Flights": { pt: "Flights", en: "Flights" },
  "Shafts": { pt: "Shafts", en: "Shafts" },
  "Cases": { pt: "Cases", en: "Cases" },
  "Estolas": { pt: "Estolas", en: "Cases" },
  "Mesas": { pt: "Mesas", en: "Tables" },
  "Suportes": { pt: "Suportes", en: "Stands" },
  "Iluminação": { pt: "Iluminação", en: "Lighting" },
  "Proteção": { pt: "Proteção", en: "Protection" },
  "Manutenção": { pt: "Manutenção", en: "Maintenance" },
  "Ferramentas": { pt: "Ferramentas", en: "Tools" },
  "Roupas": { pt: "Roupas", en: "Clothing" },
  "Camisolas": { pt: "Camisolas", en: "Shirts" },
  "Merchandising": { pt: "Merchandising", en: "Merchandise" },
};

// Product name translations (for common/generic product names)
export const productTranslations: TranslationMap = {
  // Common product terms
  "Conjunto de Dardos": { pt: "Conjunto de Dardos", en: "Dart Set" },
  "Set de Dardos": { pt: "Set de Dardos", en: "Dart Set" },
  "Alvo": { pt: "Alvo", en: "Dartboard" },
  "Alvo Electrónico": { pt: "Alvo Electrónico", en: "Electronic Dartboard" },
  "Alvo Tradicional": { pt: "Alvo Tradicional", en: "Traditional Dartboard" },
  "Pontas de Aço": { pt: "Pontas de Aço", en: "Steel Tips" },
  "Pontas Soft": { pt: "Pontas Soft", en: "Soft Tips" },
  "Pontas de Plástico": { pt: "Pontas de Plástico", en: "Plastic Tips" },
  "Estola": { pt: "Estola", en: "Case" },
  "Caixa": { pt: "Caixa", en: "Box" },
  "Mesa de Dardos": { pt: "Mesa de Dardos", en: "Dart Table" },
  "Suporte": { pt: "Suporte", en: "Stand" },
  "Protetor": { pt: "Protetor", en: "Protector" },
  "Ferramenta": { pt: "Ferramenta", en: "Tool" },
  "Chave": { pt: "Chave", en: "Tool" },
  "Camisola": { pt: "Camisola", en: "Shirt" },
  "T-Shirt": { pt: "T-Shirt", en: "T-Shirt" },
  "Profissional": { pt: "Profissional", en: "Professional" },
  "Iniciante": { pt: "Iniciante", en: "Beginner" },
  "Avançado": { pt: "Avançado", en: "Advanced" },
  "Pack": { pt: "Pack", en: "Pack" },
  "Kit": { pt: "Kit", en: "Kit" },
};

// Brand name translations (if needed)
export const brandTranslations: TranslationMap = {
  // Usually brand names stay the same across languages
  // Example: "Harrows": { pt: "Harrows", en: "Harrows" },
};

/**
 * Translates content based on the translation maps
 * @param content - The original content (usually in Portuguese)
 * @param language - Target language ('pt' or 'en')
 * @param translationMap - The translation map to use
 * @returns Translated content or original content if no translation found
 */
export function translateContent(
  content: string,
  language: 'pt' | 'en',
  translationMap: TranslationMap
): string {
  if (language === 'pt') {
    return content; // Return original Portuguese content
  }

  // Look for exact match first
  const translation = translationMap[content];
  if (translation) {
    return translation[language] || content;
  }

  // If no exact match, try partial matches for compound names
  let translatedContent = content;
  Object.keys(translationMap).forEach(key => {
    if (content.includes(key)) {
      const translation = translationMap[key];
      if (translation) {
        translatedContent = translatedContent.replace(key, translation[language] || key);
      }
    }
  });

  return translatedContent;
}

/**
 * Translates a category name
 */
export function translateCategoryName(name: string, language: 'pt' | 'en'): string {
  return translateContent(name, language, categoryTranslations);
}

/**
 * Translates a product name
 */
export function translateProductName(name: string, language: 'pt' | 'en'): string {
  return translateContent(name, language, productTranslations);
}

/**
 * Translates a product description
 */
export function translateProductDescription(description: string, language: 'pt' | 'en'): string {
  return translateContent(description, language, productTranslations);
}

/**
 * Translates a brand name
 */
export function translateBrandName(name: string, language: 'pt' | 'en'): string {
  return translateContent(name, language, brandTranslations);
}